import { z } from 'zod/v4';

/**
 * A file reference in a write body. Only `id` is used: the audit report and CAP
 * file are immutable after creation (DELTA-2543), so the server compares the id
 * against the stored one and rejects any change. `url`/`bucketName` are still
 * required because the legacy schemas required them and every frontend caller
 * sends the full file object.
 */
const AuditFileRefSchema = z.looseObject({
  id: z.string().min(1),
  url: z.string().min(1),
  bucketName: z.string().min(1),
});

const dateTime = z.iso.datetime({ offset: true });

/**
 * `PUT /api/audits/:id` body. Migrated from the legacy audits-service
 * `PUT /v1/audits/:id`, whose JSON schema had the same required fields.
 *
 * PUT replaces the record, so the two file refs follow the guard's `replace`
 * mode: a null or omitted `correctiveActionPlanFile` on an audit that has a
 * CAP file counts as clearing it, and is rejected.
 *
 * The legacy schema set `additionalProperties: true` and the handler spread the
 * body over the stored row, so any column could be overwritten:
 * `auditReportFileId` (which bypassed the file guard), `isPublic`,
 * `dataStatus`, `name`, `type`. `z.object` drops unknown keys instead. The old
 * dashboard also sends `createCapa`, which the server never read; it is dropped
 * the same way.
 */
export const UpdateAuditBodySchema = z.object({
  auditDate: dateTime,
  nextPlannedAuditDate: dateTime,
  auditedBy: z.string().min(1),
  facilityScore: z.string().min(1).max(255),
  referenceNumber: z.string().min(1).nullable().optional(),
  auditReportFile: AuditFileRefSchema,
  correctiveActionPlanFile: AuditFileRefSchema.nullable().optional(),
});

export type UpdateAuditBody = z.infer<typeof UpdateAuditBodySchema>;

/**
 * `PATCH /api/audits/:id` body. Migrated from the legacy audits-service
 * `PATCH /v1/audits/:id`. Every field is optional, and an omitted field keeps
 * its stored value. `facilityScore`/`referenceNumber` accept `null` to clear.
 *
 * The file refs follow the guard's `patch` mode: absent or null means "no
 * change", the stored id is a no-op, and any other id is rejected. They are
 * never written.
 *
 * `isPublic` replaced `PATCH /v3/audits/make-public` (DELTA-2545).
 *
 * The legacy schema set `additionalProperties: false`, but Fastify's default
 * Ajv strips extra keys rather than rejecting them. `z.object` behaves the same.
 */
export const PatchAuditBodySchema = z.object({
  name: z.string().min(1).optional(),
  auditDate: dateTime.optional(),
  nextPlannedAuditDate: dateTime.optional(),
  auditedBy: z.string().min(1).optional(),
  facilityScore: z.string().min(1).nullable().optional(),
  referenceNumber: z.string().min(1).nullable().optional(),
  isPublic: z.boolean().optional(),
  auditReportFile: AuditFileRefSchema.nullable().optional(),
  correctiveActionPlanFile: AuditFileRefSchema.nullable().optional(),
});

export type PatchAuditBody = z.infer<typeof PatchAuditBodySchema>;

/**
 * The audit returned by `PUT` and `PATCH /api/audits/:id`, rebuilt the same way
 * as the create response (unscoped full read, then preview URLs and
 * `privacyStatus`). Only the fields a caller can rely on are declared;
 * `looseObject` lets the rest of the row through unchanged. Dates are ISO
 * strings.
 *
 * `isPublic` is a boolean here, unlike the detail read's raw 0/1. The legacy
 * response schema declared it boolean, so Fastify coerced it, and the old
 * dashboard's audit lists replace a table row with this response.
 */
export const AuditWriteResponseSchema = z.looseObject({
  id: z.string(),
  companyId: z.string().nullable(),
  auditTypeId: z.string().nullable(),
  name: z.string().nullable(),
  auditDate: z.string().nullable(),
  nextPlannedAuditDate: z.string().nullable(),
  auditedBy: z.string().nullable(),
  facilityScore: z.string().nullable(),
  referenceNumber: z.string().nullable(),
  auditReportFileId: z.string().nullable(),
  correctiveActionPlanFileId: z.string().nullable(),
  isPublic: z.boolean(),
  updatedAt: z.string().nullable(),
});

export type AuditWriteResponse = z.infer<typeof AuditWriteResponseSchema>;
